import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Plus, 
  Calculator, 
  FileText, 
  Edit, 
  Trash2, 
  Download,
  Save,
  Upload,
  AlertTriangle,
  CheckCircle,
  X,
  DollarSign,
  TrendingUp,
  Edit2,
  Check
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import AutocompleteInput from "./AutocompleteInput";
import AssessmentPreview from "./AssessmentPreview";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CaseDashboard({ caseId, onNavigateBack, currentUser }) {
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false); // For valuation processing overlay
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  
  // Claim item management
  const [newClaim, setNewClaim] = useState({
    qty: 1,
    description: "",
    maker: "",
    model: "",
    age: 0,
    life_span: 20,
    damage_code: 1,
    image_base64: ""
  });
  
  // Assessment results
  const [assessmentResults, setAssessmentResults] = useState(null);
  const [blexbotRequestNumber, setBlexbotRequestNumber] = useState(null);
  // Per-line accept/reject/modified decisions, keyed by row index. Lifted
  // from AssessmentPreview so the Case Summary can derive the overall case
  // status from them.
  const [lineDecisions, setLineDecisions] = useState({});
  const [manualInputItems, setManualInputItems] = useState({});
  
  // Claim list visibility (hide after processing)
  const [showClaimList, setShowClaimList] = useState(true);
  
  // Save/update state
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Edit price functionality
  const [editingPrices, setEditingPrices] = useState({}); // Track which items are being edited
  const [editedPrices, setEditedPrices] = useState({}); // Store edited prices
  
  // Edit claim item functionality
  const [editingItem, setEditingItem] = useState(null); // Track which claim item is being edited
  const [editedItemData, setEditedItemData] = useState({}); // Store edited claim item data
  
  // View management
  const [currentView, setCurrentView] = useState("dashboard"); // dashboard, preview

  // Refs for stream + results — the interval timer and the SSE handler
  // read these so they never clobber each other or restart on every
  // render.
  const activeEventSourceRef = useRef(null);
  const assessmentResultsRef = useRef(null);
  useEffect(() => {
    assessmentResultsRef.current = assessmentResults;
  }, [assessmentResults]);
  // Close any lingering EventSource on component unmount so we don't
  // leak a background stream when the user navigates away.
  useEffect(() => () => {
    try { activeEventSourceRef.current && activeEventSourceRef.current.close(); } catch {}
    activeEventSourceRef.current = null;
  }, []);
  
  // Batch input
  const [batchText, setBatchText] = useState("");
  
  // Input form visibility
  const [selectedInputMethod, setSelectedInputMethod] = useState(""); // "", "manual", "batch"

  useEffect(() => {
    if (caseId) {
      loadCase();
    }
  }, [caseId]);

  // SSE stream subscription. Returns a Promise that resolves on terminal
  // state (`completed` / `requires_attention` / `end`). On a transient
  // network / stream error we DON'T reject — instead we silently reconnect
  // after 5 seconds (up to 5 retries) so the user never sees a flashing
  // "Stream disconnected" message during a brief hiccup. Only an explicit
  // backend error payload (parsed from `ev.data`) bubbles up.
  //
  // The active EventSource is tracked in `activeEventSourceRef` so a
  // second subscribe call (e.g. retry, resume on reload) closes the old
  // stream first — guaranteeing only one SSE connection per component.
  const subscribeAssessmentStream = (assessmentId, { startedAt, switchToPreview, buildPlaceholder }) => {
    // Close any pre-existing stream before we open a new one.
    try { activeEventSourceRef.current && activeEventSourceRef.current.close(); } catch {}
    activeEventSourceRef.current = null;

    const sessionToken = (() => {
      try { return sessionStorage.getItem("pv_session_token") || ""; } catch { return ""; }
    })();
    return new Promise((resolve, reject) => {
      const streamUrl = `${API}/assess-claims/${assessmentId}/stream?session=${encodeURIComponent(sessionToken)}`;
      const MAX_RETRIES = 5;
      const RETRY_DELAY_MS = 5000;
      let retries = 0;
      let reconnectTimer = null;
      let es = null;
      let settled = false;

      const settle = (fn) => {
        if (settled) return;
        settled = true;
        if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
        try { es && es.close(); } catch {}
        if (activeEventSourceRef.current === es) activeEventSourceRef.current = null;
        fn();
      };

      const scheduleReconnect = () => {
        if (settled) return;
        try { es && es.close(); } catch {}
        if (retries >= MAX_RETRIES) {
          settle(() => reject(new Error("Lost connection to the assessment stream.")));
          return;
        }
        retries += 1;
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(() => {
          if (settled) return;
          connect();
        }, RETRY_DELAY_MS);
      };

      const connect = () => {
        es = new EventSource(streamUrl);
        activeEventSourceRef.current = es;

        es.onopen = () => { retries = 0; };

        es.onmessage = async (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data?.blexbot_request_number) {
              setBlexbotRequestNumber(data.blexbot_request_number);
            }
            if (data?.status === "researching") {
              const haveRows = Array.isArray(data?.result?.results) && data.result.results.length > 0;
              if (haveRows) {
                setAssessmentResults(data.result);
              } else {
                // Only paint a placeholder when the screen is still empty.
                // NEVER replace already-rendered quoted rows with
                // zero-price placeholders — that's the "flash to Working…"
                // regression we fought off in the backend merge guard.
                const existing = assessmentResultsRef.current;
                const existingRows = existing?.results || [];
                if (existingRows.length === 0 && buildPlaceholder) {
                  setAssessmentResults(buildPlaceholder("researching"));
                }
              }
              if (switchToPreview && startedAt != null && Date.now() - startedAt >= 10000) {
                switchToPreview();
              }
              return;
            }
            if (data?.status === "completed" || data?.status === "requires_attention") {
              setAssessmentResults(data.result);
              setShowClaimList(false);
              if (switchToPreview) switchToPreview();
              await loadCase();
              settle(resolve);
              return;
            }
          } catch (parseErr) {
            console.error("Malformed SSE payload:", parseErr);
          }
        };

        es.addEventListener("end", () => settle(resolve));

        // Named "error" events carry a backend-side error payload — those
        // are terminal and should bubble up to the caller.
        es.addEventListener("error", (ev) => {
          let msg = "";
          try {
            const payload = JSON.parse(ev.data || "{}");
            if (payload?.detail) msg = payload.detail;
            if (payload?.status === 401) window.dispatchEvent(new Event("pv:session-expired"));
          } catch {}
          // Native connection errors (no payload) are handled once by `onerror`.
          if (msg) settle(() => reject(new Error(msg)));
        });

        // Low-level connection drop (network blip, server restart) —
        // schedule a 5s reconnect instead of bubbling up a disconnection
        // error to the UI.
        es.onerror = () => {
          if (es && es.readyState === EventSource.CLOSED) {
            scheduleReconnect();
          }
        };
      };

      connect();
    });
  };

  const loadCase = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cases/${caseId}`);
      setCaseData(response.data);
      setEditedTitle(response.data.title);

      // If case has assessment results, set them
      if (response.data.assessment_result) {
        setAssessmentResults(response.data.assessment_result);
      }
      // Restore persisted Accept/Reject/Modified decisions on load.
      if (response.data.line_decisions) {
        setLineDecisions(response.data.line_decisions);
      }

      // Auto-resume: if the case is still researching, re-attach to the SSE
      // stream for its active assessment so progress keeps updating even
      // after a page reload / navigation. This guards against the bug where
      // Blexbot finished mid-session and the UI stayed stuck on
      // "Researching" because the previous stream had closed.
      if (String(response.data.status || "").toLowerCase() === "researching") {
        try {
          const sessionToken = (() => {
            try { return sessionStorage.getItem("pv_session_token") || ""; } catch { return ""; }
          })();
          const activeResp = await axios.get(
            `${API}/cases/${caseId}/active-assessment`,
            { headers: { "X-PV-Session": sessionToken } }
          );
          const active = activeResp?.data;
          if (active?.assessment_id && active?.is_active !== false) {
            if (active?.blexbot_request_number) {
              setBlexbotRequestNumber(active.blexbot_request_number);
            }
            // Seed placeholder rows so the UI shows one row per claim item.
            const buildPlaceholder = (lineStatus) => ({
              results: (response.data.claim_items || []).map((ci) => ({
                original_claim: ci,
                matched_product: null,
                match_confidence: 0,
                match_details: "Blexbot is sourcing this item…",
                market_price: 0,
                depreciation_amount: 0,
                damage_amount: 0,
                final_payout: 0,
                requires_manual_input: false,
                source_url: null,
                image_url: null,
                status: lineStatus,
              })),
              total_claims: (response.data.claim_items || []).length,
              total_payout: 0,
            });
            if (!response.data.assessment_result) {
              setAssessmentResults(buildPlaceholder("researching"));
            }
            // Attach the SSE stream in the background — don't block loadCase.
            subscribeAssessmentStream(active.assessment_id, {
              startedAt: Date.now(),
              switchToPreview: null, // user is already on the dashboard view
              buildPlaceholder,
            }).catch((e) => console.warn("resume stream ended:", e.message));
          }
        } catch (resumeErr) {
          console.warn("could not resume assessment stream:", resumeErr?.message);
        }
      }
    } catch (error) {
      console.error("Error loading case:", error);
      setError("Failed to load case. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const updateCaseTitle = async () => {
    try {
      await axios.put(`${API}/cases/${caseId}`, {
        title: editedTitle
      });
      setCaseData(prev => ({ ...prev, title: editedTitle }));
      setIsEditing(false);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error updating case title:", error);
      setError("Failed to update case title");
    }
  };

  // Re-poll Blexbot for any lines still marked as "Working…" (no_match) and
  // merge the fresh result into UI state. Called automatically every 10
  // seconds while the Assessment Preview is open. When `silent` is true
  // (the auto-refresh path) and the response contains newly-quoted prices,
  // we flash a tiny bottom-right "Updated just now" toast so the user knows
  // fresh data landed without being interrupted.
  const [refreshingPartial, setRefreshingPartial] = useState(false);
  const refreshInFlightRef = useRef(false);
  const refreshPartialAssessment = async ({ silent = false } = {}) => {
    if (!caseId || refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    try {
      if (!silent) setRefreshingPartial(true);
      const sessionToken = (() => {
        try { return sessionStorage.getItem("pv_session_token") || ""; } catch { return ""; }
      })();
      const beforeRows = assessmentResultsRef.current?.results || [];
      const resp = await axios.post(
        `${API}/cases/${caseId}/refresh-partial`,
        {},
        { headers: { "X-PV-Session": sessionToken } }
      );
      const freshCase = resp?.data?.case;
      if (freshCase) {
        setCaseData(freshCase);
        if (freshCase.assessment_result) {
          const nextRows = freshCase.assessment_result?.results || [];
          // Detect "something new happened" by comparing per-row market_price
          // and status against the pre-refresh snapshot. Any delta on any
          // line is enough to tell the user prices refreshed.
          const changed = nextRows.some((nr, i) => {
            const br = beforeRows[i] || {};
            const nPrice = Number(nr?.market_price ?? nr?.matched_product?.market_price ?? 0);
            const bPrice = Number(br?.market_price ?? br?.matched_product?.market_price ?? 0);
            const nStatus = String(nr?.status || "").toLowerCase();
            const bStatus = String(br?.status || "").toLowerCase();
            return nPrice !== bPrice || nStatus !== bStatus;
          });
          setAssessmentResults(freshCase.assessment_result);
          if (silent && changed) {
            toast.success("Updated just now", { duration: 2200 });
          }
        }
        if (freshCase.line_decisions) {
          setLineDecisions(freshCase.line_decisions);
        }
      }
    } catch (err) {
      console.warn("refresh-partial failed:", err?.response?.data?.detail || err?.message);
    } finally {
      refreshInFlightRef.current = false;
      setRefreshingPartial(false);
    }
  };

  // Persist Accept/Reject/Modified choices to Mongo so a refresh doesn't
  // wipe them. Debounced via requestAnimationFrame so quickly-tapped
  // buttons collapse into a single PUT.
  const saveLineDecisions = async (nextDecisions) => {
    if (!caseId) return;
    try {
      const sessionToken = (() => {
        try { return sessionStorage.getItem("pv_session_token") || ""; } catch { return ""; }
      })();
      const resp = await axios.put(
        `${API}/cases/${caseId}/line-decisions`,
        { line_decisions: nextDecisions || {} },
        { headers: { "X-PV-Session": sessionToken } }
      );
      if (resp?.data) {
        // Keep the Case Summary's effective_payout fresh so the right
        // sidebar's green total updates the moment a row is rejected.
        setCaseData((prev) => ({ ...(prev || {}), ...resp.data }));
      }
    } catch (err) {
      console.warn("save line_decisions failed:", err?.response?.data?.detail || err?.message);
    }
  };

  // Wrap the raw setLineDecisions so every change gets persisted.
  const handleLineDecisionsChange = (updater) => {
    setLineDecisions((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      // Persist in the background without blocking UI.
      saveLineDecisions(next);
      return next;
    });
  };

  // Keep Blexbot's session cookies alive — ping every 10 minutes while
  // the case is open so long browsing sessions don't force the user back
  // through the WhatsApp OTP flow.
  useEffect(() => {
    const sessionToken = (() => {
      try { return sessionStorage.getItem("pv_session_token") || ""; } catch { return ""; }
    })();
    if (!sessionToken) return undefined;
    const ping = () => {
      axios
        .post(`${API}/session/keep-alive`, {}, { headers: { "X-PV-Session": sessionToken } })
        .catch((err) => {
          // 401 means the upstream Blexbot session is already gone — surface
          // once to the console; the next real request will trigger a visible
          // sign-in prompt.
          if (err?.response?.status === 401) {
            console.warn("Blexbot session expired — user needs to sign in again.");
          }
        });
    };
    ping(); // once on mount
    const t = setInterval(ping, 10 * 60 * 1000); // every 10 min
    return () => clearInterval(t);
  }, []);

  // Auto-refresh from Blexbot every 10s while the Assessment Preview is open.
  // Pauses while the tab is hidden and refreshes immediately when it returns.
  useEffect(() => {
    if (currentView !== "preview" || !caseId) return undefined;
    let t = null;
    const tick = () => refreshPartialAssessment({ silent: true });
    const start = () => { if (!t) t = setInterval(tick, 10 * 1000); };
    const stop = () => { if (t) { clearInterval(t); t = null; } };
    const onVisibility = () => {
      if (document.hidden) { stop(); } else { tick(); start(); }
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { stop(); document.removeEventListener("visibilitychange", onVisibility); };
  }, [caseId, currentView]); // eslint-disable-line

  const saveCase = async () => {
    try {
      setSaving(true);
      setError("");
      
      await axios.put(`${API}/cases/${caseId}`, {
        title: caseData.title,
        claim_items: caseData.claim_items || []
      });
      
      // Show success feedback
      setSaveSuccess(true);
      setHasUnsavedChanges(false);
      
      // Hide success message after 2 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 2000);
      
      // Reload case data to ensure consistency
      await loadCase();
      
    } catch (error) {
      console.error("Error saving case:", error);
      setError("Failed to save case. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const startEditingPrice = (resultIndex, currentPrice) => {
    setEditingPrices(prev => ({ ...prev, [resultIndex]: true }));
    setEditedPrices(prev => ({ ...prev, [resultIndex]: currentPrice }));
  };

  const cancelEditingPrice = (resultIndex) => {
    setEditingPrices(prev => {
      const newState = { ...prev };
      delete newState[resultIndex];
      return newState;
    });
    setEditedPrices(prev => {
      const newState = { ...prev };
      delete newState[resultIndex];
      return newState;
    });
  };

  const saveEditedPrice = async (resultIndex) => {
    try {
      const newPrice = parseFloat(editedPrices[resultIndex]);
      if (isNaN(newPrice) || newPrice < 0) {
        setError("Please enter a valid price");
        return;
      }

      // Update the assessment results with the new price
      const updatedResults = { ...assessmentResults };
      const result = updatedResults.results[resultIndex];
      
      // Update replacement value and recalculate
      result.market_price = newPrice;
      
      // Recalculate depreciation (20% per year, max 80%)
      const depreciationRate = Math.min(result.original_claim.age * 0.2, 0.8);
      result.depreciation_amount = newPrice * depreciationRate;
      
      // Recalculate damage replacement value based on damage code
      const depreciatedValue = newPrice - result.depreciation_amount;
      const payoutPercentages = { 1: 1.0, 2: 0.5, 3: 0.2 };
      const payoutPercentage = payoutPercentages[result.original_claim.damage_code] || 0;
      result.final_payout = depreciatedValue * payoutPercentage * result.original_claim.qty;
      
      // Recalculate total payout
      updatedResults.total_payout = updatedResults.results.reduce(
        (sum, r) => sum + r.final_payout, 0
      );
      
      // Update state
      setAssessmentResults(updatedResults);
      
      // Update the case in the backend
      await axios.put(`${API}/cases/${caseId}`, {
        assessment_result: updatedResults,
        total_payout: updatedResults.total_payout,
        status: "completed"
      });
      
      // Clear editing state
      cancelEditingPrice(resultIndex);
      setHasUnsavedChanges(false);
      
      // Show success feedback
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      
    } catch (error) {
      console.error("Error saving price:", error);
      setError("Failed to save price changes");
    }
  };

  // Shared research helper used by BOTH the single-add and batch flows so
  // the DB-match criteria are provably identical: lookup by description,
  // narrow with maker+model when provided, attach the matching product image
  // when one is found. Returns the item (mutated in place for convenience).
  const enrichClaimItemWithMatch = async (item) => {
    if (!item?.description) return item;
    try {
      const resp = await axios.get(
        `${API}/autocomplete/description?query=${encodeURIComponent(item.description)}`
      );
      const suggestions = resp.data?.suggestions || [];
      if (suggestions.length > 0) {
        let bestMatch = suggestions[0];
        if (item.maker || item.model) {
          const exact = suggestions.find(
            (s) =>
              (s.maker && s.maker.toLowerCase() === (item.maker || "").toLowerCase()) &&
              (s.model && s.model.toLowerCase() === (item.model || "").toLowerCase())
          );
          if (exact) bestMatch = exact;
        }
        if (bestMatch && bestMatch.image_base64 && !item.image_base64) {
          item.image_base64 = bestMatch.image_base64;
        }
      }
    } catch (lookupErr) {
      // Non-fatal — add the item without an image rather than block it.
      console.log(`Could not fetch image for: ${item.description}`);
    }
    return item;
  };

  const addClaimItem = async () => {
    if (!newClaim.description.trim()) {
      setError("Description is required");
      return;
    }

    try {
      const prepared = {
        ...newClaim,
        qty: parseInt(newClaim.qty),
        age: parseInt(newClaim.age),
        life_span: parseInt(newClaim.life_span),
        damage_code: parseInt(newClaim.damage_code),
        image_base64: newClaim.image_base64 || "",
      };
      await enrichClaimItemWithMatch(prepared);

      const updatedItems = [...(caseData.claim_items || []), prepared];
      await axios.put(`${API}/cases/${caseId}`, { claim_items: updatedItems });

      await loadCase();
      setHasUnsavedChanges(true);
      setNewClaim({
        qty: 1,
        description: "",
        maker: "",
        model: "",
        age: 0,
        life_span: 20,
        damage_code: 1,
        image_base64: ""
      });
      setSelectedInputMethod("");
      setError("");
    } catch (error) {
      console.error("Error in addClaimItem:", error);
      setError(`Failed to add claim item: ${error.response?.data?.detail || error.message}`);
    }
  };

  const removeClaimItem = async (index) => {
    try {
      const updatedItems = caseData.claim_items.filter((_, i) => i !== index);
      
      await axios.put(`${API}/cases/${caseId}`, {
        claim_items: updatedItems
      });

      await loadCase();
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error("Error removing claim item:", error);
      setError("Failed to remove claim item");
    }
  };

  const startEditingItem = (index) => {
    setEditingItem(index);
    setEditedItemData({ ...caseData.claim_items[index] });
  };

  const cancelEditingItem = () => {
    setEditingItem(null);
    setEditedItemData({});
  };

  const saveEditedItem = async (index) => {
    try {
      const updatedItems = [...caseData.claim_items];
      updatedItems[index] = {
        ...editedItemData,
        qty: parseInt(editedItemData.qty),
        age: parseInt(editedItemData.age),
        life_span: parseInt(editedItemData.life_span),
        damage_code: parseInt(editedItemData.damage_code)
      };
      
      await axios.put(`${API}/cases/${caseId}`, {
        claim_items: updatedItems
      });

      await loadCase();
      setEditingItem(null);
      setEditedItemData({});
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error("Error updating claim item:", error);
      setError("Failed to update claim item");
    }
  };

  const processClaims = async () => {
    if (!caseData.claim_items || caseData.claim_items.length === 0) {
      setError("Please add claim items before processing");
      return;
    }

    try {
      setProcessing(true); // Show processing overlay
      setError("");

      // Build an immediate placeholder result list so we can switch to the
      // Assessment Preview quickly (within 10 seconds) and show per-line
      // Pending / Researching / Quoted status badges while Blexbot works.
      const buildPlaceholder = (lineStatus) => ({
        results: (caseData.claim_items || []).map((ci) => ({
          original_claim: ci,
          matched_product: null,
          match_confidence: 0,
          match_details: "Blexbot is sourcing this item…",
          market_price: 0,
          depreciation_amount: 0,
          damage_amount: 0,
          final_payout: 0,
          requires_manual_input: false,
          source_url: null,
          image_url: null,
          status: lineStatus,
        })),
        total_claims: (caseData.claim_items || []).length,
        total_payout: 0,
      });

      // Kick off a Blexbot-backed assessment. The response is an assessment_id
      // that we poll until Blexbot's sourcing team marks the request quoted.
      const sessionToken = (() => {
        try { return sessionStorage.getItem("pv_session_token") || ""; } catch { return ""; }
      })();
      const submit = await axios.post(
        `${API}/cases/${caseId}/assess`,
        {},
        { headers: { "X-PV-Session": sessionToken } }
      );
      const assessmentId = submit?.data?.assessment_id;
      if (!assessmentId) {
        throw new Error("Blexbot did not return an assessment id.");
      }

      // Seed the preview with "pending" rows so the user sees progress now.
      setAssessmentResults(buildPlaceholder("pending"));

      // Switch to the Assessment Preview after 10 s even if Blexbot is still
      // researching — polling continues in the background and the rows
      // auto-upgrade from "researching" → "quoted" as data arrives.
      const AUTO_SWITCH_MS = 10000;
      const startedAt = Date.now();
      let switchedToPreview = false;
      const switchToPreview = () => {
        if (switchedToPreview) return;
        switchedToPreview = true;
        setShowClaimList(false);
        setCurrentView("preview");
        setProcessing(false); // dismiss the full-screen spinner
      };
      const switchTimer = setTimeout(switchToPreview, AUTO_SWITCH_MS);

      // Server-Sent Events — delegate to the shared helper so processClaims
      // (new submissions) and loadCase (resume after reload) share the same
      // stream-handling code. The helper resolves on terminal state and
      // rejects on error.
      try {
        await subscribeAssessmentStream(assessmentId, {
          startedAt,
          switchToPreview,
          buildPlaceholder,
        });
      } finally {
        clearTimeout(switchTimer);
      }
    } catch (error) {
      console.error("Error processing claims:", error);
      setError(
        error?.response?.data?.detail ||
          error?.message ||
          "Failed to process claims. Please try again."
      );
    } finally {
      setProcessing(false); // Hide processing overlay
    }
  };

  const handleBatchTextInput = async () => {
    if (!batchText.trim()) {
      setError("Please paste your claim data");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const lines = batchText.trim().split('\n');
      const newItems = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        const parts = line.split('\t').map(p => p.trim());
        // Only require qty and description (minimum 2 columns)
        if (parts.length >= 2 && parts[1]) {
          newItems.push({
            qty: parseInt(parts[0]) || 1,
            description: parts[1] || "",
            maker: parts[2] || "",
            model: parts[3] || "",
            age: parseInt(parts[4]) || 0,
            life_span: parseInt(parts[5]) || 20,
            damage_code: parseInt(parts[6]) || 1,
            image_base64: "" // Will be filled if product match found
          });
        }
      }

      if (newItems.length === 0) {
        setError("No valid claim items found. Format: Qty[TAB]Description[TAB]Maker[TAB]Model (Maker and Model are optional)");
        return;
      }

      // Use the SHARED enrichClaimItemWithMatch helper so batch items follow
      // the exact same research criteria as single-add items (lookup by
      // description, narrow with maker+model, attach matched image).
      // Fetching in parallel to keep large batches responsive.
      await Promise.all(newItems.map((item) => enrichClaimItemWithMatch(item)));

      const allItems = [...(caseData.claim_items || []), ...newItems];
      
      await axios.put(`${API}/cases/${caseId}`, {
        claim_items: allItems
      });

      await loadCase();
      setHasUnsavedChanges(true);
      setBatchText("");
      
      // Hide the input method form after adding items
      setSelectedInputMethod("");
      
      setError("");
    } catch (error) {
      console.error("Error processing batch input:", error);
      setError("Failed to process batch input");
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    if (!assessmentResults) {
      setError("Please process claims first before exporting");
      return;
    }

    try {
      const response = await axios.post(`${API}/export-pdf`, assessmentResults, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${caseData.case_number}_assessment.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      setError("Failed to export PDF. Please try again.");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDamageText = (code) => {
    const damageTypes = {
      1: "Total loss",
      2: "Clean on site",
      3: "Clean and inspect on site",
      4: "Clean and handle on site", 
      5: "Clean, inspect, and handle on site",
      6: "Clean, inspect, repair, and handle on site",
      7: "Remove, transport off site, clean, inspect, and reinstall",
      8: "Remove, transport off site, clean, inspect, repair, and reinstall",
      9: "No damage",
      10: "Belongs to a third party, insured not liable"
    };
    return damageTypes[code] || `Code ${code}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !caseData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading case...</p>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Case not found</p>
          <Button onClick={onNavigateBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Render assessment preview if in preview mode
  if (currentView === "preview" && assessmentResults) {
    return (
      <div className="page-transition">
        <AssessmentPreview 
          assessmentResults={assessmentResults}
          caseData={caseData}
          currentUser={currentUser}
          onNavigateBack={() => setCurrentView("dashboard")}
          onExportPDF={exportPDF}
          lineDecisions={lineDecisions}
          onLineDecisionsChange={handleLineDecisionsChange}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Image Hover Effect Styles */}
      <style>{`
        .product-image-hover {
          transition: transform 0.3s ease, z-index 0s 0s;
          position: relative;
          cursor: pointer;
        }
        .product-image-hover:hover {
          transform: scale(1.5);
          z-index: 50;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
        }
      `}</style>
      
      {/* Header */}
      <div className="bg-white border-b-2 border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_dc1c0b21-5011-47e1-a19d-11de6678deed/artifacts/55cbyjpt_Logo.png" 
                alt="PriceValuator Logo" 
                className="h-48 object-contain"
              />
              <Button 
                variant="ghost" 
                onClick={onNavigateBack}
                className="text-[#1E3A5F] hover:bg-gray-100 rounded-full px-4 py-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
              <div>
                <div className="flex items-center space-x-3">
                  {isEditing ? (
                    <div className="flex items-center space-x-2">
                      <Input
                        value={editedTitle}
                        onChange={(e) => {
                          setEditedTitle(e.target.value);
                          setHasUnsavedChanges(true);
                        }}
                        className="text-xl font-semibold"
                      />
                      <Button size="sm" onClick={updateCaseTitle}>
                        <Save className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <h1 className="text-2xl font-bold text-[#1E3A5F]">{caseData.title}</h1>
                      <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)} className="text-cyan-600 hover:bg-cyan-50">
                        <Edit className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  <Badge className={getStatusColor(caseData.status)}>
                    {caseData.status}
                  </Badge>
                </div>
                <p className="text-gray-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>Case {caseData.case_number} • Created {formatDate(caseData.created_at)}</span>
                  {(caseData.blexbot_request_number || blexbotRequestNumber) && (
                    <span
                      data-testid="case-header-radicado"
                      className="inline-flex items-center text-xs font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 px-2 py-0.5 rounded-full"
                      title="Blexbot sourcing reference number"
                    >
                      ID # {caseData.blexbot_request_number || blexbotRequestNumber}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <Alert className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {saveSuccess && (
          <Alert className="mb-6 border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              Case updated successfully!
            </AlertDescription>
          </Alert>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Input Forms */}
          <div className="lg:col-span-2 space-y-6">
            {/* Input Method Selection */}
            <Card className="rounded-2xl border-0 shadow-lg bg-white hover:shadow-xl transition-all duration-300">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-t-2xl">
                <CardTitle className="text-indigo-900">Add products</CardTitle>
                <CardDescription className="text-indigo-600">
                  Choose how you want to add items to this case
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                {!selectedInputMethod ? (
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Add Single Product Button */}
                    <button
                      onClick={() => setSelectedInputMethod("manual")}
                      className="group relative p-6 bg-gradient-to-br from-cyan-50 to-blue-50 hover:from-cyan-100 hover:to-blue-100 rounded-2xl border-2 border-cyan-200 hover:border-cyan-400 transition-all duration-300 hover:shadow-xl hover:scale-105 text-left"
                    >
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <Plus className="w-7 h-7 text-white" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-cyan-700 transition-colors">
                            Add Single Product
                          </h3>
                          <p className="text-sm text-gray-600">
                            Enter product details manually one at a time with autocomplete assistance
                          </p>
                        </div>
                      </div>
                      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </button>

                    {/* Add Products Batch Button */}
                    <button
                      onClick={() => setSelectedInputMethod("batch")}
                      className="group relative p-6 bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 rounded-2xl border-2 border-purple-200 hover:border-purple-400 transition-all duration-300 hover:shadow-xl hover:scale-105 text-left"
                    >
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <Upload className="w-7 h-7 text-white" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-700 transition-colors">
                            Add Products Batch
                          </h3>
                          <p className="text-sm text-gray-600">
                            Copy and paste multiple products from spreadsheet or list format
                          </p>
                        </div>
                      </div>
                      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between mb-4 pb-4 border-b-2 border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        selectedInputMethod === 'manual' 
                          ? 'bg-gradient-to-br from-cyan-500 to-blue-600' 
                          : 'bg-gradient-to-br from-purple-500 to-pink-600'
                      }`}>
                        {selectedInputMethod === 'manual' ? (
                          <Plus className="w-5 h-5 text-white" />
                        ) : (
                          <Upload className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {selectedInputMethod === 'manual' ? 'Add Single Product' : 'Add Products Batch'}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {selectedInputMethod === 'manual' ? 'Manual input mode' : 'Batch input mode'}
                        </p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={() => setSelectedInputMethod("")}
                      className="rounded-full border-gray-300 hover:bg-gray-100"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Change Method
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Manual Input Card - Only show when selected */}
            {selectedInputMethod === "manual" && (
              <Card>
                <CardHeader>
                  <CardTitle>Manual Input</CardTitle>
                  <CardDescription>
                    Add individual equipment items for insurance claim assessment
                  </CardDescription>
                </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <Input
                    type="number"
                    min="1"
                    value={newClaim.qty}
                    onChange={(e) => setNewClaim({...newClaim, qty: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <AutocompleteInput
                    field="description"
                    source="blexbot"
                    value={newClaim.description}
                    onChange={(value) => setNewClaim({...newClaim, description: value})}
                    onSelect={(product) => {
                      // Blexbot payload → form fields. Fall back to legacy
                      // (local-DB) field names so the component still works
                      // for the pre-existing /autocomplete/{field} path.
                      setNewClaim({
                        ...newClaim,
                        description: product.nombre || product.value,
                        maker: product.marca || product.maker || "",
                        model: product.modelo || product.model || "",
                        reference: product.referencia || "",
                        price: product.precio || "",
                        details: product.descripcion || "",
                        source_url: product.link || "",
                        image_base64: product.imagen || product.image_base64 || "",
                      });
                    }}
                    placeholder="Start typing to search Blexbot catalog…"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Maker <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <AutocompleteInput
                      field="maker"
                      value={newClaim.maker}
                      onChange={(value) => setNewClaim({...newClaim, maker: value})}
                      placeholder="Enter maker/brand (optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Model <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <AutocompleteInput
                      field="model"
                      value={newClaim.model}
                      onChange={(value) => setNewClaim({...newClaim, model: value})}
                      placeholder="Enter model (optional)"
                    />
                  </div>
                </div>

                <Button onClick={addClaimItem} className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Claim Item
                </Button>
              </CardContent>
            </Card>
            )}

            {/* Batch Input Card - Only show when selected */}
            {selectedInputMethod === "batch" && (
              <Card>
                <CardHeader>
                  <CardTitle>Batch Input</CardTitle>
                  <CardDescription>
                    Copy and paste multiple claim items from a spreadsheet
                  </CardDescription>
                </CardHeader>
              <CardContent>
                <textarea
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Paste your data here (Tab-separated: Qty[TAB]Description[TAB]Maker[TAB]Model)&#10;Note: Only Qty and Description are required. Maker and Model are optional.&#10;Example:&#10;1[TAB]Commercial Range[TAB]Vulcan[TAB]SX36-6BP&#10;2[TAB]Dishwasher"
                  value={batchText}
                  onChange={(e) => setBatchText(e.target.value)}
                />
                <div className="flex justify-between items-center mt-4">
                  <div className="text-sm text-gray-600">
                    <strong>Required:</strong> Qty, Description &nbsp;|&nbsp; <strong>Optional:</strong> Maker, Model <span className="text-gray-400">(tab-separated)</span>
                  </div>
                  <Button onClick={handleBatchTextInput} disabled={!batchText.trim()}>
                    <Upload className="w-4 h-4 mr-2" />
                    Add Items
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Current Items - with edit/delete functionality */}
            {caseData.claim_items && caseData.claim_items.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Products List ({caseData.claim_items.length})</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowClaimList(!showClaimList)}
                    >
                      {showClaimList ? "Hide Items" : "Show Items"}
                    </Button>
                  </div>
                </CardHeader>
                {showClaimList && (
                  <CardContent>
                    <div className="space-y-3">
                      {caseData.claim_items.map((item, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          {editingItem === index ? (
                            /* Edit Mode */
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                                <Input
                                  type="number"
                                  min="1"
                                  value={editedItemData.qty}
                                  onChange={(e) => setEditedItemData({...editedItemData, qty: e.target.value})}
                                  className="text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                                <Input
                                  type="text"
                                  value={editedItemData.description}
                                  onChange={(e) => setEditedItemData({...editedItemData, description: e.target.value})}
                                  className="text-sm"
                                  placeholder="Enter equipment description"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Maker <span className="text-gray-400 font-normal">(Optional)</span>
                                  </label>
                                  <Input
                                    type="text"
                                    value={editedItemData.maker}
                                    onChange={(e) => setEditedItemData({...editedItemData, maker: e.target.value})}
                                    className="text-sm"
                                    placeholder="Enter maker/brand"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Model <span className="text-gray-400 font-normal">(Optional)</span>
                                  </label>
                                  <Input
                                    type="text"
                                    value={editedItemData.model}
                                    onChange={(e) => setEditedItemData({...editedItemData, model: e.target.value})}
                                    className="text-sm"
                                    placeholder="Enter model"
                                  />
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 pt-2">
                                <Button
                                  size="sm"
                                  onClick={() => saveEditedItem(index)}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={cancelEditingItem}
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            /* View Mode */
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3 flex-1">
                                {/* Product Image */}
                                {item.image_base64 ? (
                                  <img 
                                    src={item.image_base64} 
                                    alt={item.description}
                                    className="w-12 h-12 object-cover rounded border border-gray-300 product-image-hover"
                                  />
                                ) : (
                                  <div className="w-12 h-12 border border-dashed border-gray-300 rounded flex items-center justify-center bg-gray-50">
                                    <span className="text-xs text-gray-400">No img</span>
                                  </div>
                                )}
                                {/* Product Info */}
                                <div className="flex-1">
                                  <div className="font-medium">{item.description}</div>
                                  <div className="text-sm text-gray-600 mt-1">
                                    {item.maker} {item.model}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => startEditingItem(index)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeClaimItem(index)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}

            {/* Process Valuation Button - Only show when there are items */}
            {caseData.claim_items && caseData.claim_items.length > 0 && (
              <div className="flex justify-center mt-6">
                <Button 
                  onClick={processClaims} 
                  disabled={loading || processing}
                  className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-full px-10 py-4 shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-lg"
                >
                  <Calculator className="w-5 h-5 mr-2" />
                  {processing ? "Blexbot is researching…" : (loading ? "Processing..." : "Process Valuation")}
                </Button>
              </div>
            )}
          </div>

          {/* Right Column - Case Summary */}
          <div className="space-y-6">
            <Card className="rounded-2xl border-0 shadow-lg bg-gradient-to-br from-indigo-50 to-purple-50 hover:shadow-xl transition-all duration-300">
              <CardHeader className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-t-2xl">
                <CardTitle className="text-indigo-900">Case Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    {(() => {
                      // Case is "completed" only when every assessed line is
                      // both quoted by Blexbot and marked Accept by the user.
                      // Otherwise it stays "open" (even if Blexbot finished).
                      const results = assessmentResults?.results || [];
                      let displayStatus = caseData.status;
                      if (results.length > 0) {
                        const allQuotedAndAccepted = results.every((r, idx) => {
                          const quoted = String(r.status || "").toLowerCase() === "quoted";
                          const accepted = lineDecisions[idx] === "accept";
                          return quoted && accepted;
                        });
                        displayStatus = allQuotedAndAccepted ? "completed" : "open";
                      }
                      return (
                        <Badge
                          className={getStatusColor(displayStatus)}
                          data-testid="case-summary-status"
                        >
                          {displayStatus}
                        </Badge>
                      );
                    })()}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Items:</span>
                    <span className="font-medium">{caseData.total_items || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Assessment:</span>
                    <span className="font-bold text-green-600" data-testid="case-summary-total">
                      {formatCurrency(
                        caseData.effective_payout !== undefined && caseData.effective_payout !== null
                          ? caseData.effective_payout
                          : caseData.total_payout
                      )}
                    </span>
                  </div>
                  {Object.values(lineDecisions || {}).includes("reject") && (
                    <div className="flex justify-between text-xs text-rose-700">
                      <span>Rejected rows excluded</span>
                      <span className="line-through text-gray-500">
                        {formatCurrency(caseData.total_payout || 0)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Updated:</span>
                    <span className="text-sm">{formatDate(caseData.updated_at)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {assessmentResults && (() => {
              // The Assessment summary reflects Blexbot's sourcing work:
              // "Assessment Complete!" once every row is quoted (regardless
              // of accept decisions). The acceptance step is a separate
              // user-review stage surfaced as a secondary line of text.
              // (Case Summary → Status still waits for quoted AND accepted
              // before it flips to "completed".)
              const results = assessmentResults.results || [];
              const totalLines = results.length;
              const quotedCount = results.filter(
                (r) => String(r.status || "").toLowerCase() === "quoted"
              ).length;
              const acceptedCount = results.filter(
                (r, idx) =>
                  String(r.status || "").toLowerCase() === "quoted" &&
                  lineDecisions[idx] === "accept"
              ).length;
              const pendingBlexbot = Math.max(totalLines - quotedCount, 0);
              const isAllQuoted = totalLines > 0 && pendingBlexbot === 0;
              const needsReview = isAllQuoted && acceptedCount < totalLines;
              const partialPayout = results.reduce((sum, r) => {
                const quoted = String(r.status || "").toLowerCase() === "quoted";
                return sum + (quoted ? (r.final_payout || 0) : 0);
              }, 0);

              const headerGrad = isAllQuoted
                ? "from-green-100 to-emerald-100"
                : "from-amber-100 to-orange-100";
              const cardGrad = isAllQuoted
                ? "from-green-50 to-emerald-50"
                : "from-amber-50 to-orange-50";
              const titleColor = isAllQuoted ? "text-green-900" : "text-amber-900";
              const iconColor = isAllQuoted ? "text-green-600" : "text-amber-600";
              const headlineColor = isAllQuoted ? "text-green-700" : "text-amber-700";
              const payoutColor = isAllQuoted ? "text-green-800" : "text-amber-800";

              return (
                <Card
                  data-testid="assessment-summary-card"
                  className={`rounded-2xl border-0 shadow-lg bg-gradient-to-r ${cardGrad} hover:shadow-xl transition-all duration-300`}
                >
                  <CardHeader className={`bg-gradient-to-r ${headerGrad} rounded-t-2xl`}>
                    <div className="flex items-center justify-center">
                      <CheckCircle className={`w-8 h-8 ${iconColor} mr-2`} />
                      <CardTitle
                        className={titleColor}
                        data-testid="assessment-summary-title"
                      >
                        {isAllQuoted ? "Assessment Complete!" : "Assessment in process"}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 text-center">
                    <div className="space-y-4">
                      <p className={`${headlineColor} mb-2`}>
                        {isAllQuoted
                          ? needsReview
                            ? "Blexbot delivered the valuation. Review each line and click Accept to finalise."
                            : "Your price assessment has been processed and accepted."
                          : "Blexbot is still sourcing some of your items — results will stream in as they're priced."}
                      </p>
                      {blexbotRequestNumber && (
                        <div
                          data-testid="blexbot-request-number"
                          className={`text-sm font-semibold ${titleColor} bg-white/60 inline-block px-3 py-1 rounded-full`}
                        >
                          Request # {blexbotRequestNumber}
                        </div>
                      )}
                      <div
                        className={`text-3xl font-bold ${payoutColor}`}
                        data-testid="assessment-summary-payout"
                      >
                        {formatCurrency(partialPayout)}
                      </div>
                      <p className="text-gray-600 text-sm" data-testid="assessment-summary-counts">
                        {isAllQuoted
                          ? `${totalLines} items quoted · ${acceptedCount}/${totalLines} accepted`
                          : `${quotedCount} quoted · ${pendingBlexbot} still researching`}
                      </p>

                      <div className="pt-4">
                        <Button 
                          onClick={() => setCurrentView("preview")}
                          data-testid="view-detailed-results-btn"
                          className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-full px-6 py-3 font-semibold"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          View Detailed Assessment Results
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t mt-16">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <p className="text-center text-gray-600 text-sm">
            ClaimValuator! Quickly calculate claim values with up-to-date market prices from our 1M+ product database.
          </p>
        </div>
      </footer>

      {/* Processing Overlay */}
      {processing && (
        <div className="processing-overlay">
          <div className="text-center">
            <div className="pulse-scale">
              <div className="mx-auto w-20 h-20 mb-6">
                <svg className="spinner w-20 h-20 text-cyan-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">Blexbot is Sourcing Your Claim</h2>
            <p className="text-cyan-200 text-lg">Live-checking suppliers and pricing — this can take a few minutes.</p>
            <p className="text-cyan-300/70 text-sm mt-2">Feel free to leave this page open; results appear automatically once sourcing completes.</p>
            <div className="mt-6 flex justify-center space-x-2">
              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CaseDashboard;